import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { GlobalStyles } from './styles/GlobalStyles';
import { I18nProvider } from './locales/useI18n';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { analytics } from './services/analytics';
import { useAnalytics, usePageViewTracking } from './hooks/useAnalytics';
import AnalyticsConsentBanner from './components/AnalyticsConsent';
import EnvironmentBanner from './components/EnvironmentBanner';
import { getPageAnalyticsContext } from './services/analyticsContext';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import HowItWorksPage from './pages/HowItWorksPage';
import LatestUpdatesPage from './pages/LatestUpdatesPage';
import BrowsePage from './pages/BrowsePage';
import MapPage from './pages/MapPage';
import MyEventsPage from './pages/MyEventsPage';
import EventDetailsPage from './pages/EventDetailsPage';
import EntityEventsPage from './pages/EntityEventsPage';
import LocationEventsPage from './pages/LocationEventsPage';
import CreateEventGroupPage from './pages/CreateEventGroupPage';
import EventGroupPage from './pages/EventGroupPage';
import PrivacyPage from './pages/PrivacyPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import LegacyEntityRedirect from './pages/LegacyEntityRedirect';
import EventCategoriesPage from './pages/EventCategoriesPage';
import StatisticsPage from './pages/StatisticsPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminFunctionsPage from './pages/AdminFunctionsPage';
import AdminReprocessPage from './pages/AdminReprocessPage';
import AdminRefreshEventSourcesPage from './pages/AdminRefreshEventSourcesPage';
import AdminHowItWorksPage from './pages/AdminHowItWorksPage';
import AdminSystemDashboardPage from './pages/AdminSystemDashboardPage';
import AdminSearchHealthPage from './pages/AdminSearchHealthPage';
import AdminSyncIndexPage from './pages/AdminSyncIndexPage';
import AdminCleanupOrphansPage from './pages/AdminCleanupOrphansPage';
import AdminDuplicatesPage from './pages/AdminDuplicatesPage';
import AdminWorkflowMetadataPage from './pages/AdminWorkflowMetadataPage';
import AdminWipeEventsPage from './pages/AdminWipeEventsPage';
import AdminCachePage from './pages/AdminCachePage';
import AdminProcessingRunsPage from './pages/AdminProcessingRunsPage';
import AdminNightlyRunsPage from './pages/AdminNightlyRunsPage';
import AdminExtractionBatchesPage from './pages/AdminExtractionBatchesPage';
import AdminBatchVerificationPage from './pages/AdminBatchVerificationPage';
import AdminCatalogPage from './pages/AdminCatalogPage';
import AdminMergeEntitiesPage from './pages/AdminMergeEntitiesPage';
import AdminAiDecisionsPage from './pages/AdminAiDecisionsPage';
import AdminAgentPage from './pages/AdminAgentPage';
import { colors, spacing, breakpoints } from './styles/theme';
import { AppMetadataProvider } from './contexts/AppMetadataContext';
import { RememberedEventsProvider } from './contexts/RememberedEventsContext';

// Component to scroll to top on route changes
function ScrollToTop() {
  const location = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  
  return null;
}

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${colors.background.primary};
`;

const MainContent = styled.main`
  flex: 1;
  width: 100%;
`;

const MobileNavOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${props => props.isOpen ? 'block' : 'none'};
  
  @media (min-width: ${breakpoints.md}) {
    display: none;
  }
`;

const MobileNav = styled.nav.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 280px;
  background: ${colors.white};
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.1);
  transform: translateX(${props => props.isOpen ? '0' : '100%'});
  transition: transform 0.3s ease;
  z-index: 1000;
  padding: ${spacing[6]} ${spacing[4]};
  
  @media (min-width: ${breakpoints.md}) {
    display: none;
  }
`;

const MobileNavHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing[6]};
  padding-bottom: ${spacing[4]};
  border-bottom: 1px solid ${colors.border.light};
`;

const MobileNavTitle = styled.h3`
  margin: 0;
  color: ${colors.text.primary};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${colors.text.secondary};
  cursor: pointer;
  padding: ${spacing[1]};
  
  &:hover {
    color: ${colors.text.primary};
  }
`;

const MobileNavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const MobileNavItem = styled.li`
  margin-bottom: ${spacing[2]};
`;

const MobileNavLink = styled.button`
  display: block;
  width: 100%;
  padding: ${spacing[3]} ${spacing[2]};
  color: ${colors.text.primary};
  text-decoration: none;
  border: none;
  background: transparent;
  border-radius: 8px;
  transition: background-color 0.15s ease;
  text-align: left;
  cursor: pointer;
  font-size: 16px;
  
  &:hover {
    background: ${colors.background.secondary};
  }
  
  &.active {
    background: ${colors.primary[50]};
    color: ${colors.primary[600]};
  }
`;

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <GlobalStyles />
          <AppContent />
        </Router>
      </AuthProvider>
    </I18nProvider>
  );
}

function AppContent() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [analyticsReady, setAnalyticsReady] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo, isLoading: isAuthLoading } = useAuth();
  const { trackEvent } = useAnalytics();
  const isAdminRoute = location.pathname.startsWith('/admin');
  usePageViewTracking(analyticsReady);

  useEffect(() => {
    if (!analyticsReady || isAuthLoading || !userInfo.isAuthenticated || !userInfo.userId) return;
    const role = userInfo.isAdmin ? 'administrator' : userInfo.isContributor ? 'contributor' : 'authenticated';
    const page = getPageAnalyticsContext(location.pathname, location.search);
    analytics.identifyAuthenticatedUser(userInfo.userId, role, page.route_template);
  }, [analyticsReady, isAuthLoading, location.pathname, location.search, userInfo]);

  // Initialize analytics services
  useEffect(() => {
    const initializeAnalytics = async () => {
      const analyticsConfig = {
        googleAnalytics: process.env.REACT_APP_GA_MEASUREMENT_ID ? {
          measurementId: process.env.REACT_APP_GA_MEASUREMENT_ID
        } : undefined,
        applicationInsights: process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING ? {
          connectionString: process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING
        } : undefined,
        clarity: process.env.REACT_APP_CLARITY_PROJECT_ID ? {
          projectId: process.env.REACT_APP_CLARITY_PROJECT_ID
        } : undefined,
      };

      // Only initialize if at least one service is configured
      if (analyticsConfig.googleAnalytics || analyticsConfig.applicationInsights || analyticsConfig.clarity) {
        try {
          await analytics.initialize(analyticsConfig);
          
          // Log analytics status in development
          if (process.env.NODE_ENV === 'development') {
            console.log('Analytics initialized:', analytics.getStatus());
          }
        } catch (error) {
          console.error('Failed to initialize analytics:', error);
        } finally {
          setAnalyticsReady(true);
        }
      } else {
        console.log('No analytics services configured. Add environment variables to enable tracking.');
        setAnalyticsReady(true);
      }
    };

    initializeAnalytics();
  }, []);

  const handleMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleMobileNavigation = (path: string) => {
    const action = path === '/groups/new'
      ? 'create_group_click'
      : path === '/map'
        ? 'map_click'
        : 'mobile_navigation_click';
    trackEvent({ category: 'navigation', action, custom_parameters: { action_location: 'mobile_menu', destination: path } });
    navigate(path);
    closeMobileMenu();
  };

  return (
    <AppMetadataProvider enabled={!isAdminRoute}>
      <RememberedEventsProvider enabled={!isAdminRoute}>
      <AppContainer>
      <EnvironmentBanner />
      {!isAdminRoute && (
        <>
          <Header
            onMenuToggle={handleMenuToggle}
            isMenuOpen={isMobileMenuOpen}
          />

          <MobileNavOverlay
            isOpen={isMobileMenuOpen}
            onClick={closeMobileMenu}
          />
          <MobileNav isOpen={isMobileMenuOpen}>
            <MobileNavHeader>
              <MobileNavTitle>Navigation</MobileNavTitle>
              <CloseButton onClick={closeMobileMenu}>
                ✕
              </CloseButton>
            </MobileNavHeader>
            <MobileNavList>
              <MobileNavItem>
                <MobileNavLink onClick={() => handleMobileNavigation('/')}>
                  Home
                </MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink onClick={() => handleMobileNavigation('/groups/new')}>
                  Create Event Groups
                </MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink onClick={() => handleMobileNavigation('/my-events')}>
                  My Events
                </MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink onClick={() => handleMobileNavigation('/about')}>
                  About
                </MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink onClick={() => handleMobileNavigation('/how-it-works')}>
                  How It Works
                </MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink onClick={() => handleMobileNavigation('/latest-updates')}>
                  Latest Updates
                </MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink onClick={() => handleMobileNavigation('/browse')}>
                  Browse Events
                </MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink onClick={() => handleMobileNavigation('/map')}>
                  Events Near Me
                </MobileNavLink>
              </MobileNavItem>
            </MobileNavList>
          </MobileNav>
        </>
      )}
      
      <MainContent data-clarity-mask={isAdminRoute ? 'true' : undefined}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/latest-updates" element={<LatestUpdatesPage />} />
          {/* `?entity=` is a legacy link shape; it 302s to the canonical entity page. */}
          <Route path="/browse" element={<LegacyEntityRedirect><BrowsePage /></LegacyEntityRedirect>} />
          <Route path="/categories" element={<EventCategoriesPage />} />
          <Route path="/map" element={<MapPage />} />
          <Route path="/my-events" element={<MyEventsPage />} />
          <Route path="/statistics" element={<StatisticsPage />} />
          <Route path="/groups/new" element={<CreateEventGroupPage />} />
          <Route path="/group/:groupSlug" element={<EventGroupPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookies" element={<CookiePolicyPage />} />
          {/* The numeric id is the identity; the trailing slug is descriptive and
              is rewritten to the current title by the detail page itself. */}
          <Route path="/event/:eventId" element={<EventDetailsPage />} />
          <Route path="/event/:eventId/:eventSlug" element={<EventDetailsPage />} />

          {/* Admin Routes - Protected */}
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminDashboardPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/functions" 
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminFunctionsPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/admin/system-insights"
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminSystemDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/how-it-works"
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminHowItWorksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/refresh-event-sources"
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminRefreshEventSourcesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/extract-events"
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminReprocessPage />
              </ProtectedRoute>
            }
          />
          {/* Older bookmarked path for the extraction job. */}
          <Route
            path="/admin/reprocess"
            element={<Navigate to="/admin/extract-events" replace />}
          />
          <Route
            path="/admin/search-health"
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminSearchHealthPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/sync-index"
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminSyncIndexPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/cleanup-orphans"
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminCleanupOrphansPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/duplicates"
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminDuplicatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/workflow-metadata"
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminWorkflowMetadataPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/wipe-events"
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminWipeEventsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/cache"
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminCachePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/processing-runs"
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminProcessingRunsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/nightly-runs"
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminNightlyRunsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/batches"
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminExtractionBatchesPage />
              </ProtectedRoute>
            }
          />
          {process.env.NODE_ENV === 'development' && (
            <Route
              path="/admin/batches/verify"
              element={
                <ProtectedRoute requireRole="administrator">
                  <AdminBatchVerificationPage />
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path="/admin/catalog"
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminCatalogPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/merge-entities"
            element={
              <ProtectedRoute requireRole="administrator">
                <AdminMergeEntitiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ai-decisions"
            element={
              <ProtectedRoute requireRole="administrator">
                <AdminAiDecisionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/agent"
            element={
              <ProtectedRoute requireRole="contributor">
                <AdminAgentPage />
              </ProtectedRoute>
            }
          />
          
          {/* Canonical location events route. Declared before the generic
              entity route so "location" is never read as an entity type. */}
          <Route path="/location/:citySlug/events" element={<LocationEventsPage />} />

          {/* Canonical entity events route. Declared last among public routes so
              the fixed paths above always win the two-segment match. */}
          <Route path="/:entityType/:entitySlug/events" element={<EntityEventsPage />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </MainContent>
      
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && <AnalyticsConsentBanner />}
      </AppContainer>
      </RememberedEventsProvider>
    </AppMetadataProvider>
  );
}

export default App;
